import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/store/auth'
import { getAppConfig } from '@/config'
import { useToast } from '@/composables/useToast'
import type { ServiceType } from '@/constants/environments'

function getBaseUrl(service: ServiceType): string {
  const config = getAppConfig()
  switch (service) {
    case 'main':
      return config.mainApiUrl
    case 'sections':
      return config.sectionsApiUrl
    case 'langschool':
      return config.langschoolApiUrl
  }
}

export function useSchoolLogo(service: ServiceType, schoolId: string) {
  const auth = useAuthStore()

  return useQuery({
    queryKey: ['school-logo', service, schoolId],
    queryFn: async () => {
      const baseUrl = getBaseUrl(service)
      const response = await fetch(
        `${baseUrl}/api/v1/school-logo?school_id=${encodeURIComponent(schoolId)}`,
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to fetch logo')
      }
      const blob = await response.blob()
      return URL.createObjectURL(blob)
    },
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUploadSchoolLogo(service: ServiceType) {
  const auth = useAuthStore()
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async ({
      schoolId,
      file,
    }: {
      schoolId: string
      file: File
    }) => {
      const formData = new FormData()
      formData.append('school_id', schoolId)
      formData.append('logo', file)

      const baseUrl = getBaseUrl(service)
      const response = await fetch(`${baseUrl}/api/v1/school-logo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
        },
        body: formData,
      })
      if (!response.ok) {
        throw new Error('Failed to upload logo')
      }
      return response.json()
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['school-logo', service, variables.schoolId] })
      toast.success('Logo uploaded successfully')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to upload logo')
    },
  })
}
